/**
 * Park Color Theming
 * 
 * Each Disney World park has a unique color scheme that is applied
 * to all entities belonging to that park.
 */

import { DISNEY_PARKS } from './themeparks'

export interface ParkColorScheme {
  name: string
  bg: string // Background color (Tailwind class)
  bgHex: string // Background color (hex)
  text: string // Text color (Tailwind class)
  border: string // Border color (Tailwind class)
  hover: string // Hover background color (Tailwind class)
  gradient: string // Gradient classes
}

// Map park external IDs to color schemes
// Colors inspired by the RateWDW logo (#FEDB1A yellow, #E79D20 gold, #AA6C2A bronze, #1E4289 blue, #55B1F1 light blue)
export const PARK_COLORS: Record<string, ParkColorScheme> = {
  [DISNEY_PARKS.MAGIC_KINGDOM]: {
    name: 'Magic Kingdom',
    bg: 'bg-[#eadfff]/70',
    bgHex: '#eadfffff',
    text: '!text-slate-900',
    border: 'border-[#eadfff]/50',
    hover: 'hover:bg-[#eadfff]/80',
    gradient: 'from-[#eadfff]/70 to-[#eadfff]/90',
  },
  [DISNEY_PARKS.EPCOT]: {
    name: 'EPCOT',
    bg: 'bg-[#9c9fb1]/70',
    bgHex: '#9c9fb1ff',
    text: '!text-slate-900',
    border: 'border-[#9c9fb1]/50',
    hover: 'hover:bg-[#9c9fb1]/80',
    gradient: 'from-[#9c9fb1]/70 to-[#9c9fb1]/90',
  },
  [DISNEY_PARKS.HOLLYWOOD_STUDIOS]: {
    name: 'Hollywood Studios',
    bg: 'bg-[#cebba7]/70',
    bgHex: '#cebba7ff',
    text: '!text-slate-900',
    border: 'border-[#cebba7]/50',
    hover: 'hover:bg-[#cebba7]/80',
    gradient: 'from-[#cebba7]/70 to-[#cebba7]/90',
  },
  [DISNEY_PARKS.ANIMAL_KINGDOM]: {
    name: 'Animal Kingdom',
    bg: 'bg-[#a6cfc2]/70',
    bgHex: '#a6cfc2ff',
    text: '!text-slate-900',
    border: 'border-[#a6cfc2]/50',
    hover: 'hover:bg-[#a6cfc2]/80',
    gradient: 'from-[#a6cfc2]/70 to-[#a6cfc2]/70',
  },
  [DISNEY_PARKS.TYPHOON_LAGOON]: {
    name: 'Typhoon Lagoon',
    bg: 'bg-[#93b9c0]/70',
    bgHex: '#93b9c0ff',
    text: '!text-slate-900',
    border: 'border-[#93b9c0]/50',
    hover: 'hover:bg-[#93b9c0]/80',
    gradient: 'from-[#93b9c0]/70 to-[#93b9c0]/70',
  },
  [DISNEY_PARKS.BLIZZARD_BEACH]: {
    name: 'Blizzard Beach',
    bg: 'bg-[#38bdf8]/70',
    bgHex: '#38bdf8',
    text: '!text-slate-900',
    border: 'border-[#38bdf8]/50',
    hover: 'hover:bg-[#38bdf8]/80',
    gradient: 'from-[#38bdf8]/70 to-[#38bdf8]/70',
  },
}

// Default color scheme for entities without a park
export const DEFAULT_COLOR_SCHEME: ParkColorScheme = {
  name: 'Default',
  bg: 'bg-[#ada27f]/70',
  bgHex: '#ada27fff',
  text: '!text-slate-900',
  border: 'border-[#ada27f]/50',
  hover: 'hover:bg-[#ada27f]/80',
  gradient: 'from-[#ada27f]/70 to-[#ada27f]/70',
}

/**
 * Get color scheme for a park by its external ID
 */
export function getParkColorScheme(parkExternalId: string | null | undefined): ParkColorScheme {
  if (!parkExternalId) {
    return DEFAULT_COLOR_SCHEME
  }
  return PARK_COLORS[parkExternalId] || DEFAULT_COLOR_SCHEME
}

interface ParentChain {
  externalId: string
  parent?: ParentChain | null
}

/**
 * Get the topmost parent (park) external ID from an entity
 * This traverses up the parent chain to find the park
 */
export function getTopmostParentId(entity: {
  parent?: ParentChain | null
}): string | null {
  if (!entity.parent) {
    return null
  }

  // Traverse up the parent chain
  let current: ParentChain = entity.parent
  while (current.parent) {
    current = current.parent
  }

  return current.externalId
}

/**
 * Get color scheme for an entity based on its topmost parent (park)
 */
export function getEntityColorScheme(entity: {
  externalId?: string
  parent?: ParentChain | null
}): ParkColorScheme {
  // If the entity itself is a park, use its color
  if (entity.externalId && PARK_COLORS[entity.externalId]) {
    return PARK_COLORS[entity.externalId]
  }

  // Otherwise, find the topmost parent
  const topmostParentId = getTopmostParentId(entity)
  return getParkColorScheme(topmostParentId)
}

